import type { ReactNode } from "react";
import {
  createCoreAuthClient,
  type AuthFeatureOptions,
  type AuthSocialSlot,
} from "@gsb-core/react-auth";
import { GsbEntityService, setGsbBaseDomain } from "@gsb-core/core";
import {
  createSaasConfiguration,
  createSaasServices,
} from "@gsb-core/react-saas";
import { brand } from "@gsb-core/brand";
import { resolveWorkspaceTenantCode } from "./workspace-host";

const hostname =
  typeof globalThis.location === "object"
    ? globalThis.location.hostname
    : "localhost";
function readEnv(name: string): string | undefined {
  const value: unknown = import.meta.env?.[name];
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
}

function requireEnv(name: string): string {
  const value = readEnv(name);
  if (!value) throw new Error(`${name} is required`);
  return value;
}

export const tenantCode = resolveWorkspaceTenantCode(
  hostname,
  readEnv("VITE_GSB_TENANT_CODE"),
  requireEnv("VITE_GSB_BASE_DOMAIN"),
);

const configuredApiUrl = requireEnv("VITE_GSB_API_URL");
const configuredBaseDomain = requireEnv("VITE_GSB_BASE_DOMAIN");
setGsbBaseDomain(configuredBaseDomain);
const tenantWorkspaceUrl = (code: string) =>
  `https://${code}.${configuredBaseDomain}`;
export const workspaceHref = (code: string) => `${tenantWorkspaceUrl(code)}/`;
const apiUrl = configuredApiUrl;

// Public status is intentionally pinned to the monitoring tenant, not the current workspace.
export const statusTenantCode =
  readEnv("VITE_GSB_STATUS_TENANT_CODE") ?? "dev1";

export const authClient = createCoreAuthClient(
  apiUrl,
  tenantCode,
  () => configuredApiUrl,
  {
    app: "workspace",
    appOrigin:
      typeof globalThis.location === "object"
        ? globalThis.location.origin
        : undefined,
  },
);
const entityService = GsbEntityService.getInstance();
const saasConfiguration = createSaasConfiguration({
  core: {
    apiUrl,
  },
  adapters: {
    transport: {
      runWfFunction: (request) => entityService.runWfFunction(request),
      runWorkflow: (request) => entityService.runWorkflow(request),
    },
    navigation: {
      href: (path) => path,
      navigate: (path, options) => {
        if (options?.replace) globalThis.location.replace(path);
        else globalThis.location.assign(path);
      },
    },
    storage: {
      get: (key) => globalThis.sessionStorage?.getItem(key) ?? undefined,
      remove: (key) => globalThis.sessionStorage?.removeItem(key),
      set: (key, value) =>
        globalThis.sessionStorage?.setItem(key, JSON.stringify(value)),
    },
    logger: {
      debug: () => undefined,
      error: () => undefined,
      info: () => undefined,
      warn: () => undefined,
    },
  },
});
type SaasServices = ReturnType<typeof createSaasServices>;
let saasServices: SaasServices | undefined;

export function getSaasServices(): SaasServices {
  saasServices ??= createSaasServices(saasConfiguration);
  return saasServices;
}
export const googleClientId = readEnv("VITE_GOOGLE_CLIENT_ID");

export const authShell = {
  brand: {
    name: brand.workspaceName,
    logoUrl: "/logomark.svg",
    homeHref: "/",
    tagline: brand.tagline,
  },
  legal: { terms: "/terms", privacy: "/privacy" },
} as const;

export function createAuthOptions(slots?: {
  readonly header?: ReactNode;
  readonly social?: AuthSocialSlot;
}): AuthFeatureOptions {
  return {
    client: authClient,
    tenantCode,
    registration: false,
    config: {
      loginPath: "/login",
      registerPath: "/register",
      defaultRedirect: "/dashboard",
    },
    shell: authShell,
    slots,
  };
}
